import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import MainLayout from '../../components/layout/MainLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { getScrapeResultsByQuery } from '../../lib/api';
import { canAccessDebug } from '../../lib/utils/debugAccess';
import JsonDataTable from './components/JsonDataTable';

const DebugPage = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  if (authLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!canAccessDebug(user?.email)) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const rows = await getScrapeResultsByQuery(trimmed);
      setResults(rows);
      if (rows.length === 0) {
        setError(t('debug.notFound'));
      }
    } catch (err) {
      console.error('Debug scrape lookup failed:', err);
      if (err?.code === 'INVALID_QUERY') {
        setError(t('debug.invalidQuery'));
      } else if (
        err?.code === '42501'
        || /permission denied|row-level security/i.test(String(err?.message || ''))
      ) {
        setError(t('debug.accessDenied'));
      } else {
        setError(err?.message || t('debug.fetchFailed'));
      }
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const formatScrapedAt = (value) => {
    if (!value) return t('common.emDash');
    try {
      return format(new Date(value), 'dd-MM-yyyy HH:mm:ss');
    } catch {
      return String(value);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
            {t('debug.title')}
          </h1>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm"
        >
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 min-w-0">
              <Input
                label={t('debug.queryLabel')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('debug.queryPlaceholder')}
                disabled={loading}
              />
            </div>
            <Button
              type="submit"
              loading={loading}
              disabled={!query.trim() || loading}
              iconName="Search"
              className="sm:w-auto w-full h-10"
            >
              {t('debug.search')}
            </Button>
          </div>
        </form>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {results?.length > 0 && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {t('debug.resultsCount', { count: results.length })}
            </p>

            {results.map((row, index) => (
              <article
                key={`${row.id}-${row.scrapedAt || index}`}
                className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm space-y-5"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {t('debug.resultTitle', { index: index + 1 })}
                    </h2>
                    <dl className="mt-2 grid gap-1 text-sm">
                      <div className="flex flex-wrap gap-x-2">
                        <dt className="text-muted-foreground">{t('debug.linkedinUrl')}:</dt>
                        <dd>
                          {row.linkedinUrl ? (
                            <a
                              href={row.linkedinUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline break-all"
                            >
                              {row.linkedinUrl}
                            </a>
                          ) : (
                            t('common.emDash')
                          )}
                        </dd>
                      </div>
                      <div className="flex flex-wrap gap-x-2">
                        <dt className="text-muted-foreground">{t('debug.scrapedAt')}:</dt>
                        <dd className="text-foreground">{formatScrapedAt(row.scrapedAt)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
                  <section className="space-y-3 min-w-0">
                    <h3 className="text-base font-semibold text-foreground">
                      {t('debug.rawApify')}
                    </h3>
                    <JsonDataTable
                      data={row.rawApify}
                      emptyLabel={t('debug.noApify')}
                      omitKeys={['profilePicture', 'companyLogo']}
                    />
                  </section>

                  <section className="space-y-3 min-w-0">
                    <h3 className="text-base font-semibold text-foreground">
                      {t('debug.rawBrightdata')}
                    </h3>
                    <JsonDataTable
                      data={row.rawBrightdata}
                      emptyLabel={t('debug.noBrightdata')}
                    />
                  </section>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DebugPage;
